/**
 * Phase 3 — per-message chat docs repository (client / web SDK).
 *
 * Writes one Firestore doc per message under
 * users/{uid}/chat_threads/{threadId}/messages/{messageId}, ADDITIVELY to the
 * legacy whole-array users/{uid}/chat_thread_messages/{threadId} doc. Keying by
 * messageId makes the client mirror and the server task-completion write
 * per-id upserts that cannot clobber each other. The legacy doc is never
 * deleted here, so it stays a complete fallback for the dual-read path.
 */
import { onSnapshot, setDoc, writeBatch } from 'firebase/firestore';
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import {
  Paths,
  assertCurrentUid,
  db,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter as fbStartAfter,
  withBackoff,
} from './firestoreUtils';
import {
  buildMessageDocsForThread,
  fromMessageDocsV2,
  isSettledForPerDoc,
  type ChatThreadMessageDocV2,
} from '../types/chatThreadMessageDocFirestore';

// Firestore batches cap at 500 writes; keep headroom.
const MAX_BATCH = 400;

// ── Session fingerprint cache (write-amplification guard) ───────────────────
// The per-doc store is UNCAPPED full history (Miles 2026-07-12), and the mirror
// path fans out on every persist — naively that is O(history) billed writes per
// sync. toMessageDocV2 is fully deterministic (no server timestamps), so an
// unchanged message always serializes to byte-identical JSON: remember a hash
// of each doc we SUCCESSFULLY wrote this session and skip it while unchanged.
// Hashes only (never doc bodies) so a long thread costs ~bytes per message.
// Recorded only after the batch commits — a failed batch re-sends next sync.
const writtenDocHashesByThread = new Map<string, Map<string, string>>();

function threadCacheKey(uid: string, threadId: string): string {
  return `${uid}|${threadId}`;
}

// Two independent 32-bit rolling hashes + length. A missed rewrite (collision)
// would strand a stale doc body, so a single 32-bit hash's birthday odds are
// not acceptable; the combined ~64-bit + length makes collision astronomically
// unlikely while staying allocation-free.
function docFingerprint(serialized: string): string {
  let a = 5381;
  let b = 2166136261;
  for (let i = 0; i < serialized.length; i += 1) {
    const c = serialized.charCodeAt(i);
    a = ((a << 5) + a + c) & 0xffffffff;
    b = (b ^ c) >>> 0;
    b = Math.imul(b, 16777619) >>> 0;
  }
  return `${serialized.length}:${a >>> 0}:${b}`;
}

export function __resetPerDocWriteCacheForTests(): void {
  writtenDocHashesByThread.clear();
}

/**
 * Upsert per-message docs for a thread's current messages (idempotent by
 * per-doc key). Only SETTLED messages are written (isSettledForPerDoc): a
 * still-streaming, pending, interrupted, or empty assistant copy is skipped so
 * it can never `merge:true` downgrade a finalized/anchored doc (Finding 4). The
 * skipped copy still lives in the authoritative legacy array and surfaces via
 * the dual-read union; its finalized replacement writes the converged doc.
 * Unchanged docs already written this session are skipped (fingerprint cache
 * above), so steady-state cost is O(changed messages), not O(history).
 */
export async function upsertThreadMessageDocs(
  uid: string,
  instanceId: string,
  threadId: string,
  messages: ChatMessage[],
): Promise<void> {
  addUserScopedWriteGuard(uid, 'upsertThreadMessageDocs');
  assertCurrentUid(uid, 'upsertThreadMessageDocs');
  const settled = messages.filter((m) => isSettledForPerDoc(m));
  const docs = buildMessageDocsForThread(settled, threadId, instanceId);
  if (docs.length === 0) return;

  const cacheKey = threadCacheKey(uid, threadId);
  let seen = writtenDocHashesByThread.get(cacheKey);
  if (!seen) {
    seen = new Map<string, string>();
    writtenDocHashesByThread.set(cacheKey, seen);
  }
  const dirty: Array<{ doc: ChatThreadMessageDocV2; fingerprint: string }> = [];
  for (const doc of docs) {
    const fingerprint = docFingerprint(JSON.stringify(doc));
    if (seen.get(doc.messageId) === fingerprint) continue;
    dirty.push({ doc, fingerprint });
  }
  if (dirty.length === 0) return;

  for (let i = 0; i < dirty.length; i += MAX_BATCH) {
    const slice = dirty.slice(i, i + MAX_BATCH);
    try {
      // eslint-disable-next-line no-await-in-loop
      await withBackoff(async () => {
        const batch = writeBatch(db);
        for (const { doc } of slice) {
          batch.set(Paths.chatThreadMessageDoc(uid, threadId, doc.messageId), doc, { merge: true });
        }
        await batch.commit();
      });
      for (const { doc, fingerprint } of slice) {
        seen.set(doc.messageId, fingerprint);
      }
    } catch (batchErr) {
      // A Firestore batch is ATOMIC: one rejected doc (e.g. oversized —
      // INCIDENT-20260822) used to sink every batch-mate with it. Retry the
      // slice's docs individually so the poison costs exactly one doc, and
      // name it in the log so it can be found. Individual successes ARE
      // durably written and fingerprint as such — but any residual failure
      // still REJECTS the call with the original batch error: a sync that
      // lost messages must never report success (the silent-failure pattern
      // this incident is about), and unfingerprinted docs re-send next sync.
      // eslint-disable-next-line no-console
      console.error('[chatThreadMessageDocs] batch commit failed — retrying docs individually', { threadId, size: slice.length, error: String((batchErr as any)?.message || batchErr) });
      let residualFailures = 0;
      for (const { doc, fingerprint } of slice) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await withBackoff(() => setDoc(Paths.chatThreadMessageDoc(uid, threadId, doc.messageId), doc, { merge: true }));
          seen.set(doc.messageId, fingerprint);
        } catch (docErr) {
          residualFailures += 1;
          // eslint-disable-next-line no-console
          console.error('[chatThreadMessageDocs] per-message doc REJECTED — server copy of this message is stale', { threadId, messageId: doc.messageId, error: String((docErr as any)?.message || docErr) });
        }
      }
      if (residualFailures > 0) throw batchErr;
    }
  }
}

/**
 * Delivery ladder (2026-09-08, PR-2) — the "sent" fact. ONE additive per-doc
 * write of a just-sent human bubble under its own id, AWAITED: the Firestore
 * SDK resolves a write only on server acknowledgement (with offline
 * persistence the promise simply waits for the connection), so the resolve
 * IS "the sender's copy is safe". merge:true under the same id the mirror
 * effect's `upsertThreadMessageDocs` uses, so the later batch is an
 * idempotent no-op (and the fingerprint cache is primed here). Never
 * touches the whole-array doc, so the stale-clobber gate is untouched.
 */
export async function writeOwnUserTurnNow(
  uid: string,
  instanceId: string,
  threadId: string,
  message: ChatMessage,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'writeOwnUserTurnNow');
  assertCurrentUid(uid, 'writeOwnUserTurnNow');
  if (!message || message.isUser !== true || !isSettledForPerDoc(message)) return;
  const [doc] = buildMessageDocsForThread([message], threadId, instanceId);
  if (!doc) return;
  await withBackoff(() => setDoc(Paths.chatThreadMessageDoc(uid, threadId, doc.messageId), doc, { merge: true }));
  const cacheKey = threadCacheKey(uid, threadId);
  let seen = writtenDocHashesByThread.get(cacheKey);
  if (!seen) {
    seen = new Map<string, string>();
    writtenDocHashesByThread.set(cacheKey, seen);
  }
  seen.set(doc.messageId, docFingerprint(JSON.stringify(doc)));
}

/**
 * Live listener on the NEWEST per-doc window (2026-08-11, Fernando DR
 * two-store divergence): server task completions dual-write the per-doc
 * store, but the only thread listener watched the legacy array doc — so
 * truth that existed ONLY in per-docs (e.g. a completed memo whose legacy
 * write was blocked by the client's same-id Interrupted card) was invisible
 * to every open tab until a cache clear. Same window shape as
 * getNewestThreadMessageDocsPage; local pending writes are skipped like the
 * legacy listener does.
 */
export function subscribeNewestThreadMessageDocs(
  uid: string,
  threadId: string,
  cb: (messages: ChatMessage[], fromCache: boolean) => void,
  onError?: (error: unknown) => void,
  pageSize: number = THREAD_MESSAGES_PAGE_SIZE,
): () => void {
  assertCurrentUid(uid, 'subscribeNewestThreadMessageDocs');
  const windowQuery = query(
    Paths.chatThreadMessageDocsCol(uid, threadId),
    orderBy('orderTs', 'desc'),
    fbLimit(pageSize),
  );
  return onSnapshot(windowQuery, (snap) => {
    if (snap.metadata.hasPendingWrites) return;
    const docs = snap.docs.map((d) => d.data() as ChatThreadMessageDocV2);
    cb(fromMessageDocsV2(docs), snap.metadata.fromCache);
  }, (error) => {
    onError?.(error);
  });
}

/** Read all per-message docs for a thread, ordered chronologically. */
export async function getThreadMessageDocs(uid: string, threadId: string): Promise<ChatMessage[]> {
  assertCurrentUid(uid, 'getThreadMessageDocs');
  const snap = await withBackoff(() => getDocs(Paths.chatThreadMessageDocsCol(uid, threadId)));
  const docs = snap.docs.map((d) => d.data() as ChatThreadMessageDocV2);
  return fromMessageDocsV2(docs);
}

/**
 * Cheap limit(1) existence probe (I4). Used by greet-on-focus's server-
 * emptiness verification, which only needs "is there ANY message?" — a paged
 * (or full) read would bill O(page) document reads for a boolean.
 */
export async function hasThreadMessageDocs(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'hasThreadMessageDocs');
  const probe = query(Paths.chatThreadMessageDocsCol(uid, threadId), fbLimit(1));
  const snap = await withBackoff(() => getDocs(probe));
  return !snap.empty;
}

// ── I4 (2026-07-15): paged reads — "Load earlier messages" ──────────────────
//
// The per-doc store is UNCAPPED full history (Miles 2026-07-12), so an
// unbounded read grows with thread age. The paged API reads the NEWEST window
// first and walks OLDER pages on demand.
//
// Order key: `orderTs` descending. `orderTs` is stamped by BOTH writers (the
// client mirror via toMessageDocV2 and the server task-completion write in
// functions/src/prest0nSession/taskQueue.ts) — it is the schema's primary sort
// key since Phase 3 V2, with `seq` as the in-memory tiebreaker. The QUERY
// orders by `orderTs` only (+ Firestore's implicit __name__ tiebreak), so it
// needs NO composite index — single-field auto-indexing covers it. Equal-ts
// runs are made stable across page boundaries by the __name__ tiebreak (no
// doc is skipped or duplicated on a cursor walk); WITHIN the returned set,
// fromMessageDocsV2 re-sorts by (orderTs, seq) exactly like the full read.
export const THREAD_MESSAGES_PAGE_SIZE = 60;

/**
 * Opaque cursor for walking older pages. Wraps the Firestore document
 * snapshot of the OLDEST doc in the previously-read page (snapshot cursors
 * carry the implicit __name__ tiebreak that field-value cursors would lose).
 */
export interface ThreadMessageDocsPageCursor {
  snapshot: unknown;
}

export interface ThreadMessageDocsPage {
  /** Page contents, ordered chronologically (oldest → newest). */
  messages: ChatMessage[];
  /**
   * Cursor for the next OLDER page, or null when this page provably exhausted
   * the store (page came back short). A full page yields a cursor even when
   * the store happens to end exactly at the boundary — the next walk returns
   * an empty page and a null cursor.
   */
  cursor: ThreadMessageDocsPageCursor | null;
  /** True when the page filled to `pageSize` (older docs likely exist). */
  pageFull: boolean;
}

async function fetchMessageDocsPage(
  uid: string,
  threadId: string,
  pageSize: number,
  after: ThreadMessageDocsPageCursor | null,
): Promise<ThreadMessageDocsPage> {
  const col = Paths.chatThreadMessageDocsCol(uid, threadId);
  const base = query(col, orderBy('orderTs', 'desc'), fbLimit(pageSize));
  const paged = after ? query(base, fbStartAfter(after.snapshot)) : base;
  const snap = await withBackoff(() => getDocs(paged));
  const docs = snap.docs.map((d) => d.data() as ChatThreadMessageDocV2);
  const pageFull = snap.docs.length === pageSize;
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return {
    messages: fromMessageDocsV2(docs),
    cursor: pageFull && lastDoc ? { snapshot: lastDoc } : null,
    pageFull,
  };
}

/** Read the NEWEST page of per-message docs (default ~60), newest-first query. */
export async function getNewestThreadMessageDocsPage(
  uid: string,
  threadId: string,
  pageSize: number = THREAD_MESSAGES_PAGE_SIZE,
): Promise<ThreadMessageDocsPage> {
  assertCurrentUid(uid, 'getNewestThreadMessageDocsPage');
  return fetchMessageDocsPage(uid, threadId, pageSize, null);
}

/** Read the next OLDER page after `cursor` (from a previous page read). */
export async function getOlderThreadMessageDocsPage(
  uid: string,
  threadId: string,
  cursor: ThreadMessageDocsPageCursor,
  pageSize: number = THREAD_MESSAGES_PAGE_SIZE,
): Promise<ThreadMessageDocsPage> {
  assertCurrentUid(uid, 'getOlderThreadMessageDocsPage');
  return fetchMessageDocsPage(uid, threadId, pageSize, cursor);
}
